import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { storeTokens, clearTokens } from '@/lib/api';

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const USER_CACHE_KEY = 'zv_user';
const INACTIVE_LOGOUT_FLAG = 'zv_inactive_logout';

// --- User-object cache in localStorage ---------------------------------
// Mobile Safari and Chrome often drop third-party cookies between page
// navigations — without a persistent user hint the first /auth/me fails,
// the user is bounced to /login, and the portal becomes unreachable.
// We cache the public user object (NO secrets) so the app can render the
// authenticated UI immediately and revalidate in the background.
function readUserCache() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}
function writeUserCache(user) {
  try {
    if (user && typeof user === 'object') {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    }
  } catch { /* ignore */ }
}
function clearUserCache() {
  try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  // Hydrate synchronously from localStorage to avoid the post-login flash
  // that throws mobile users back onto the login screen.
  const cachedUser = readUserCache();
  const [user, setUser] = useState(cachedUser || null);
  const [loading, setLoading] = useState(!cachedUser);
  const [sessionRemaining, setSessionRemaining] = useState(null);
  const inactivityTimer = useRef(null);
  const countdownInterval = useRef(null);
  const lastActivity = useRef(Date.now());

  const checkAuth = useCallback(async () => {
    // Skip /auth/me entirely if we don't have an access token — saves a
    // guaranteed-401 console error on every public page load.
    let hasToken = false;
    try {
      hasToken = !!localStorage.getItem('zv_at');
    } catch { /* ignore */ }
    if (!hasToken) {
      if (!readUserCache()) setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      writeUserCache(data);
    } catch {
      // Only flip to "logged out" if we had no cached user. If we did,
      // keep the cached state so a transient network / CORS hiccup on
      // mobile doesn't boot the user out of the portal.
      if (!readUserCache()) setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    setSessionRemaining(null);
  }, []);

  const performLogout = useCallback(async () => {
    clearTimers();
    // Clear client-side state FIRST so even if the server call hangs or
    // fails (slow mobile network), the next login request can never reuse
    // the previous user's token or cached identity.
    clearTokens();
    clearUserCache();
    setUser(false);
    try {
      await api.post('/auth/logout', {}, { headers: { Authorization: '' } });
    } catch { /* ignore — cookies+tokens already cleared locally */ }
  }, [clearTimers]);

  const resetInactivityTimer = useCallback(() => {
    if (!user) return;
    lastActivity.current = Date.now();
    clearTimers();
    inactivityTimer.current = setTimeout(() => {
      try { sessionStorage.setItem(INACTIVE_LOGOUT_FLAG, '1'); } catch { /* ignore */ }
      performLogout();
    }, INACTIVITY_TIMEOUT);
    countdownInterval.current = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
      setSessionRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(countdownInterval.current);
      }
    }, 1000);
    setSessionRemaining(INACTIVITY_TIMEOUT);
  }, [user, clearTimers, performLogout]);

  useEffect(() => {
    // Background revalidate (never blocks the UI thanks to cachedUser hydration).
    checkAuth();
    const handleLogout = () => {
      clearTimers();
      clearTokens();
      clearUserCache();
      setUser(false);
      setLoading(false);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => { window.removeEventListener('auth:logout', handleLogout); clearTimers(); };
  }, [checkAuth, clearTimers]);

  useEffect(() => {
    if (!user) { clearTimers(); return; }
    resetInactivityTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [user, resetInactivityTimer, clearTimers]);

  const login = async (email, password) => {
    // Nuke any stale state before authenticating — covers the "log out,
    // log back in as a different account" flow on mobile, where leftover
    // tokens would otherwise sneak into the Authorization header and
    // produce intermittent "Login fehlgeschlagen" responses.
    clearTokens();
    clearUserCache();
    const { data } = await api.post(
      '/auth/login',
      { email, password },
      // Explicitly strip Authorization so the interceptor cannot attach
      // a previous user's token onto *this* unauthenticated call.
      { headers: { Authorization: '' } }
    );
    if (!data.mfa_required) {
      if (data.access_token) {
        storeTokens(data.access_token, data.refresh_token);
      }
      // Strip tokens from the cached user object — we only want public fields.
      const publicUser = { ...data };
      delete publicUser.access_token;
      delete publicUser.refresh_token;
      writeUserCache(publicUser);
      setUser(publicUser);
    }
    return data;
  };

  const register = async (data) => {
    const resp = await api.post('/auth/register', data);
    return resp.data;
  };

  const logout = async () => {
    await performLogout();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, sessionRemaining }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
